/**
 * GateReportView — Triage Dashboard
 *
 * Shows the findings list for a gate node (Structural Check or Compile Check).
 * Supports filtering, per-finding actions, and navigation to RemediationWorkspaceView.
 *
 * Customer-facing labels:
 *   - Java Validation → "Structural Check"
 *   - Pre-Flight      → "Compile Check"
 *
 * Data source: gateReportService — fetches real gate artifacts from S3.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  ArrowLeft, Search, CheckCircle, AlertTriangle, XCircle,
  BookOpen, Loader2, Filter, ShieldCheck, Zap,
  Check, Clock, Wand2
} from 'lucide-react'
import { getAPIBaseUrl, isWebPortal } from '../../environment'
import { fetchGateFindings, deriveGateStatus } from '../../services/gates/gateReportService'
import { fetchDecisions } from '../../services/gates/gateDecisionService'
import { getAuthHeaders } from '../../services/nodeExecutors/authHelper'
import { usePreScanActions } from '../../hooks/usePreScanActions'
import RemediationWorkspaceView from './RemediationWorkspaceView'
import { useAIResolve } from '../../hooks/useAIResolve'
import AIResolveModal from '../editor/AIResolveModal'
import { useAIResolveAnalyze } from '../../hooks/useAIResolveAnalyze'
import FixCardPanel from '../editor/FixCardPanel'
import './GateReportView.css'

// Filter modes
const FILTER_BLOCKING = 'blocking'
const FILTER_WARNINGS = 'warnings'
const FILTER_ALL = 'all'

const GATE_LABELS = {
  structural: 'Structural Check',
  compile:    'Compile Check',
}

const GATE_ICONS = {
  structural: ShieldCheck,
  compile:    Zap,
}

// djb2 hash — must match gateIssueNormalizer.stableHash and decision_applier.djb2_fingerprint
function djb2(str) {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash |= 0
  }
  return (hash >>> 0).toString(16)
}

// Fingerprint for a pre-scan missing symbol (separate namespace from compile findings)
function preScanFingerprint(symbol) {
  return djb2(`pre_scan:compile:java:${symbol}`)
}

function StatusBadge({ status }) {
  const cls = status === 'PASSED' ? 'passed' : status === 'WARNINGS' ? 'warnings' : 'blocked'
  const Icon = status === 'PASSED' ? CheckCircle : status === 'WARNINGS' ? AlertTriangle : XCircle
  return (
    <span className={`gate-status-badge ${cls}`}>
      <Icon size={12} />
      {status === 'PASSED' ? 'Passed' : status === 'WARNINGS' ? 'Warnings' : 'Blocked'}
    </span>
  )
}

function FindingRow({ finding }) {
  const isApplied  = finding.state === 'applied'
  const isTodo     = finding.state === 'todo'

  return (
    <div className={`finding-row state-${finding.state.toLowerCase()}`}>
      {/* Severity */}
      <div className="finding-severity">
        <span className={`severity-badge ${finding.severity === 'BLOCK' ? 'block' : 'warn'}`}>
          {finding.severity === 'BLOCK' ? 'Blocker' : 'Warning'}
        </span>
      </div>

      {/* Location — line number only (file shown in group header) */}
      <div className="finding-locations">
        {finding.javaLocation && (
          <span className="finding-location-item">
            <code>Line {finding.javaLocation.line}</code>
          </span>
        )}
      </div>

      {/* Summary + state tag */}
      <div className="finding-main">
        <span className="finding-summary">{finding.summary}</span>
        {isApplied && (
          <span className="finding-state-tag applied">
            <Check size={10} /> Fix Applied
          </span>
        )}
        {isTodo && (
          <span className="finding-state-tag todo">
            <Clock size={10} /> Marked TODO
          </span>
        )}
      </div>
    </div>
  )
}

// ── File Group — collapsible parent row grouping errors by file ──────────────

function FileGroup({
  filename, issues, onOpenEditor, onAIResolveFile, aiResolving, gateType,
}) {
  const [collapsed, setCollapsed] = useState(false)
  const blockerCount = issues.filter(f => f.severity === 'BLOCK').length
  const warningCount = issues.filter(f => f.severity === 'WARN').length
  const hasOpenIssues = issues.some(f => f.state !== 'applied' && f.state !== 'todo')

  return (
    <div className="file-group">
      <div className="file-group-header" onClick={() => setCollapsed(!collapsed)}>
        <span className={`file-group-chevron ${collapsed ? 'collapsed' : ''}`}>&#9662;</span>
        <span className="file-group-icon">&#128196;</span>
        <span className="file-group-filename">{filename}</span>
        <span className="file-group-counts">
          {blockerCount > 0 && (
            <span className="severity-badge block">{blockerCount} error{blockerCount !== 1 ? 's' : ''}</span>
          )}
          {warningCount > 0 && (
            <span className="severity-badge warn">{warningCount} warning{warningCount !== 1 ? 's' : ''}</span>
          )}
        </span>
        <div className="file-group-actions" onClick={e => e.stopPropagation()}>
          {gateType !== 'package' && blockerCount > 0 && onAIResolveFile && (
            <button
              className="finding-action-btn gate-btn--ai"
              onClick={() => { if (isWebPortal()) return; onAIResolveFile(filename, issues); }}
              disabled={aiResolving || isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : `AI resolve all ${blockerCount} errors in ${filename}`}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Wand2 size={11} />
              AI Resolve File
            </button>
          )}
          {hasOpenIssues && (
            <button
              className="finding-action-btn"
              onClick={() => onOpenEditor(issues[0])}
              title={`Open ${filename} in the editor to fix errors`}
            >
              <BookOpen size={11} />
              Resolve
            </button>
          )}
          {!hasOpenIssues && (
            <button
              className="finding-action-btn"
              onClick={() => onOpenEditor(issues[0])}
              title={`Review ${filename} in the editor`}
            >
              <BookOpen size={11} />
              Review
            </button>
          )}
        </div>
      </div>
      {!collapsed && (
        <div className="file-group-body">
          {issues.map(finding => (
            <FindingRow
              key={finding.id}
              finding={finding}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function GateReportView({ analysisContext, onBack, onResolveIssue, onJobUpdated }) {
  const gateType  = analysisContext?.gateType || 'structural'
  const apiBase   = getAPIBaseUrl()
  const gateLabel = GATE_LABELS[gateType] || 'Gate Check'
  const GateIcon  = GATE_ICONS[gateType] || ShieldCheck

  // Prevents the useEffect([currentJobId]) from firing a duplicate fetch when
  // refreshReport itself sets currentJobId. Without this guard, every manual
  // refresh triggers a second fetch that races and can flash loading=true.
  const skipEffectRef = useRef(false)

  // Track job ID at mount time so onJobUpdated only fires on actual post-rerun changes.
  const initialJobIdRef = useRef(
    analysisContext?.jobId || analysisContext?.jobs?.java_validation_job_id || analysisContext?.jobs?.pre_flight_job_id || 'mock-job'
  )

  // Mirrors currentJobId for use inside refreshReport without adding it as a dep.
  // refreshReport intentionally excludes currentJobId from its deps to avoid stale closures;
  // this ref lets us log "what was on screen before" when a refresh is triggered.
  const currentJobIdRef = useRef(
    analysisContext?.jobId || analysisContext?.jobs?.java_validation_job_id || analysisContext?.jobs?.pre_flight_job_id || 'mock-job'
  )

  // Stable ref for onJobUpdated so refreshReport closure never goes stale.
  const onJobUpdatedRef = useRef(onJobUpdated)
  // Keep ref in sync if parent re-renders with a different callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onJobUpdatedRef.current = onJobUpdated }, [onJobUpdated])

  // Track current job ID so it can update after AI Resolve rerun
  const [currentJobId, setCurrentJobId] = useState(
    () => analysisContext?.jobId || analysisContext?.jobs?.java_validation_job_id || analysisContext?.jobs?.pre_flight_job_id || 'mock-job'
  )
  // Keep ref in sync so refreshReport can log currentJobId_at_call_time without a stale closure.
  useEffect(() => { currentJobIdRef.current = currentJobId }, [currentJobId])

  const [status, setStatus]       = useState(null)
  const [findings, setFindings]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)
  const [noImprovement, setNoImprovement] = useState(false)
  const [lastRerunJobId, setLastRerunJobId] = useState(null)  // job ID from last AI Resolve rerun
  const [rolledBackCount, setRolledBackCount] = useState(0)  // >0 = regression+rollback occurred

  // Filter state
  const [filterMode, setFilterMode] = useState(FILTER_BLOCKING)
  const [searchQuery, setSearchQuery] = useState('')

  // (strategy overrides removed — AI Resolve uses auto-detect only)

  // Remediation workspace navigation
  const [activeFinding, setActiveFinding] = useState(null) // open RemediationWorkspaceView

  // Missing symbols pre-scan (compile gate only — loaded from compile_result.json pre_scan field)
  const [missingSymbols, setMissingSymbols]                 = useState([])
  const [symbolBannerDismissed, setSymbolBannerDismissed]   = useState(false)
  // Node-level default action from DM config (pre_scan.defaults.missing_symbol_action).
  // Used to visually highlight the matching button in each banner row.
  // Does NOT auto-execute anything — just a visual hint.
  const [preScanDefaultAction, setPreScanDefaultAction]     = useState('compile-only-stub')
  // onFixNow ref — updated after handleOpenEditor is declared (avoids TDZ)
  const preScanFixNowRef = useRef(null)

  // Explicit imperative refresh — awaitable so AIResolveModal can block Close until done.
  // MUST be called with an explicit jobId — never falls back to currentJobId closure.
  // deps intentionally exclude currentJobId so handleModalCompleted is never stale.
  const refreshReport = useCallback(async (jobId) => {
    if (!jobId) {
      if (import.meta.env.DEV)
        console.warn(' [GateReportView] refreshReport called with no jobId — skipping')
      return
    }
    const reportUrl = gateType === 'compile'
      ? `${apiBase}/pre-flight/results/json/${jobId}/compile_result.json`
      : `${apiBase}/java-validation/results/json/${jobId}/java_validation_report.json`
    if (import.meta.env.DEV)
      console.log(' [GateReportView] refreshReport →', {
        gateType,
        requestedJobId:          jobId,
        currentJobId_at_call:    currentJobIdRef.current,
        willSetCurrentJobId_to:  jobId,
      })

    // Set flag BEFORE setCurrentJobId so the useEffect([currentJobId]) guard
    // fires before the dep change triggers it. Without this, setCurrentJobId
    // schedules a re-render that fires the effect with a second concurrent fetch,
    // which races against this one and can leave findings in a stale state.
    skipEffectRef.current = true
    setCurrentJobId(jobId)  // update subtitle before the fetch resolves
    setLoading(true)
    setError(null)
    try {
      // Single fetch — derive status locally to avoid a second round-trip.
      const fds = await fetchGateFindings(jobId, gateType, apiBase)
      const st  = deriveGateStatus(fds)
      const newFindings = fds.map(f => ({ ...f, state: f.state || 'OPEN' }))
      if (import.meta.env.DEV)
        console.log(' [GateReportView] refreshReport ← done', {
          gateType,
          jobId_fetched:     jobId,
          totalFindings:     fds.length,
          blockers:          fds.filter(f => f.severity === 'BLOCK').length,
          onJobUpdated_fire: jobId !== initialJobIdRef.current,
        })
      setStatus(st)
      setFindings(newFindings)
      // Notify DesignerView of pflt_NEW so viewContext stays in sync across unmount/remount
      // (e.g. user clicks Fix & Continue then Back — remount uses updated context, not stale).
      if (onJobUpdatedRef.current && jobId !== initialJobIdRef.current) {
        onJobUpdatedRef.current(jobId)
      }
      return fds.filter(f => f.severity === 'BLOCK').length
    } catch (err) {
      if (import.meta.env.DEV)
        console.error(' [GateReportView] refreshReport error', { jobId, err })
      setError(err.message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateType, apiBase])  // intentionally no currentJobId dep — always use explicit arg

  // Called by useAIResolve when rerun finishes — no-op here; subtitle + refetch
  // are handled exclusively by handleModalCompleted (via AIResolveModal onCompleted).
  const handleRerunComplete = useCallback(() => {}, [])

  // Called by AIResolveModal when rerun completes and the modal is ready to close.
  // Receives the new gate job id; refreshes report and detects no-improvement.
  // Does NOT depend on currentJobId — updatedGateJobId is always provided by the modal,
  // and findings is only needed for the beforeBlockers snapshot (stale is fine here).
  const handleModalCompleted = useCallback(async ({ updatedGateJobId, rolledBack } = {}) => {
    if (import.meta.env.DEV)
      console.log(' [GateReportView] handleModalCompleted', { updatedGateJobId, rolledBack })
    if (!updatedGateJobId) return
    const beforeBlockers = findings.filter(f => f.severity === 'BLOCK' && f.state === 'OPEN').length
    if (rolledBack?.length > 0) {
      // Regression + rollback: overlays were reverted to original.
      // Do NOT refresh to the broken rerun job — keep the original report (original error count).
      // "Open Fix & Continue" will open with the original job, showing the real error count.
      setRolledBackCount(rolledBack.length)
      setNoImprovement(true)
      return
    }
    // Normal path: refresh report to the new job
    const afterBlockers  = await refreshReport(updatedGateJobId)
    if (afterBlockers !== undefined && afterBlockers >= beforeBlockers) {
      setLastRerunJobId(updatedGateJobId)
      setNoImprovement(true)
    }
  }, [refreshReport, findings])

  // AI Resolve hook (legacy — structural check + fallback)
  const { resolveState, resolveAll, closeModal } = useAIResolve(
    analysisContext,
    apiBase,
    gateType,
    handleRerunComplete
  )

  const aiResolving = resolveState.step !== null && resolveState.step !== 'done' && resolveState.step !== 'error'

  // ── Smart AI Resolve — Human-in-the-Loop (compile gate only) ──────────────
  const {
    analyzeState,
    startAnalyze,
    applyFix,
    dismiss: dismissFixPanel,
    STATE_IDLE,
    STATE_LOADING,
    STATE_READY,
    STATE_APPLYING,
    STATE_APPLIED,
    STATE_ERROR: ANALYZE_ERROR,
  } = useAIResolveAnalyze(analysisContext, apiBase, (newJobId) => {
    setCurrentJobId(newJobId)
    refreshReport(newJobId)
  })

  // Tracks which file's fix panel is open (null = closed)
  const [fixPanelFile, setFixPanelFile] = useState(null)
  const [fixesSavedBanner, setFixesSavedBanner] = useState(null)
  const [rerunning, setRerunning] = useState(false)

  // Re-run compile check after fixes are saved
  const handleRerunCompile = useCallback(async () => {
    setRerunning(true)
    setFixesSavedBanner(null)
    try {
      const base = apiBase || getAPIBaseUrl()
      const headers = await getAuthHeaders()

      const resp = await fetch(`${base}/pre-flight/start`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scout_account_id: analysisContext?.scout_account_id,
          application_name: analysisContext?.application_name,
          user_id: analysisContext?.user_id,
          overlay_job_id: currentJobId,
        }),
      })
      if (!resp.ok) throw new Error('Failed to start compile check')
      const { job_id: newJobId } = await resp.json()

      // Poll until complete
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 5000))
        const statusResp = await fetch(`${base}/pre-flight/status/${newJobId}`, { headers })
        if (!statusResp.ok) continue
        const data = await statusResp.json()
        const st = (data.status || '').toLowerCase()
        if (st === 'completed' || st === 'succeeded' || st === 'failed') {
          setCurrentJobId(newJobId)
          refreshReport(newJobId)
          if (onJobUpdated) onJobUpdated(newJobId)
          break
        }
      }
    } catch (err) {
      console.error(' Re-run compile failed:', err)
      setFixesSavedBanner('Re-run failed: ' + err.message)
    } finally {
      setRerunning(false)
    }
  }, [apiBase, analysisContext, currentJobId, refreshReport, onJobUpdated])

  // Resolve a file using the new Smart AI Fix flow (compile only)
  const handleSmartAIResolve = useCallback((filename, fileIssues) => {
    const blockingIssues = fileIssues.filter(f => f.severity === 'BLOCK' && f.state === 'OPEN')
    if (!blockingIssues.length) return

    setFixPanelFile(filename)
    startAnalyze(currentJobId, filename, blockingIssues)
  }, [currentJobId, startAnalyze])

  // Initial load + re-fetch when jobId changes (e.g. navigating to a different job).
  // Post-AI-Resolve refreshes are driven by AIResolveModal → refreshReport directly.
  // skipEffectRef suppresses this when refreshReport itself changed currentJobId,
  // preventing a duplicate concurrent fetch that races and may leave stale state.
  useEffect(() => {
    if (skipEffectRef.current) {
      skipEffectRef.current = false
      return
    }
    refreshReport(currentJobId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId, gateType, apiBase])

  // DEV: log whenever currentJobId changes — confirms page is displaying pflt_NEW not pflt_OLD.
  // Also logs after AI Resolve All so we can confirm findings refreshed.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    console.log(' [GateReportView] displaying jobId', {
      currentJobId,
      initialJobId:  initialJobIdRef.current,
      isUpdated:     currentJobId !== initialJobIdRef.current,
      findingsCount: findings.length,
      blockers:      findings.filter(f => f.severity === 'BLOCK').length,
    })
  }, [currentJobId, findings])

  // Clear no-improvement banner when a new AI Resolve run starts.
  useEffect(() => {
    if (resolveState.open) {
      setNoImprovement(false)
      setLastRerunJobId(null)
      setRolledBackCount(0)
    }
  }, [resolveState.open])

  // Fetch pre_scan.missing_symbols from compile_result.json + existing decisions (compile gate only)
  useEffect(() => {
    if (gateType !== 'compile' || !currentJobId) return
    const accountId = analysisContext?.scout_account_id
    const appName   = analysisContext?.application_name
    setSymbolBannerDismissed(false)

    Promise.all([
      fetch(
        `${apiBase}/pre-flight/results/json/${currentJobId}/compile_result.json`,
        { headers: getAuthHeaders() }
      ).then(r => (r.ok ? r.json() : null)).catch(() => null),
      // Fetch ALL active pre-scan decisions (no gate_job_id filter) so previously
      // actioned symbols show their state even after a rerun produces a new job ID.
      (accountId && appName)
        ? fetchDecisions(accountId, appName, 'compile').catch(() => [])
        : Promise.resolve([]),
    ]).then(([data, existingDecisions]) => {
      const syms = data?.pre_scan?.missing_symbols || []
      setMissingSymbols(syms)
      resetPreScanStates(syms, existingDecisions)
      // Read node-level default action (from DM config → pre_scan.defaults)
      setPreScanDefaultAction(data?.pre_scan?.defaults?.missing_symbol_action || 'compile-only-stub')
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId, gateType, apiBase])

  // AI Resolve File — resolve all errors in a single file with ONE AI call
  const handleAIResolveFile = useCallback((filename, fileIssues) => {
    const blockingIssues = fileIssues.filter(f => f.severity === 'BLOCK' && f.state === 'OPEN')
    if (blockingIssues.length > 0) resolveAll(blockingIssues)
  }, [resolveAll])

  // Open editor action.
  // If onResolveIssue is provided (wired from DesignerView), navigate to
  // ResolveIssuesView (the real Monaco editor workspace).
  // Otherwise fall back to the internal RemediationWorkspaceView (mock textarea).
  const handleOpenEditor = useCallback((finding) => {
    if (onResolveIssue) {
      // Pass currentJobId so DesignerView can update viewContext.jobs before navigating,
      // preventing Fix & Continue from using a stale pflt_OLD job ID.
      if (import.meta.env.DEV)
        console.log(' [GateReportView] handleOpenEditor → onResolveIssue', { findingId: finding.id, currentJobId })
      onResolveIssue(finding, currentJobId)
    } else {
      setActiveFinding(finding)
    }
  }, [onResolveIssue, currentJobId])

  // Fix It Myself — open editor at the specific line (must be after handleOpenEditor)
  const handleFixMyself = useCallback((card) => {
    const syntheticFinding = {
      id:           `card-${card.file}-${card.line}`,
      summary:      card.error_message,
      severity:     'BLOCK',
      state:        'OPEN',
      javaLocation: { file: card.file, line: card.line },
    }
    handleOpenEditor(syntheticFinding)
    dismissFixPanel()
  }, [handleOpenEditor, dismissFixPanel])

  // Keep the Fix-now ref in sync so usePreScanActions always uses the latest version.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { preScanFixNowRef.current = handleOpenEditor }, [handleOpenEditor])

  // Pre-scan action hook — 3 per-row buttons (Fix now / Stub / Mark external).
  // Placed here (after handleOpenEditor) so onFixNow can safely reference it.
  const {
    actionStates,
    rerunStatus:     preScanRerunStatus,
    isRunning:       preScanRunning,
    handleFixNow:    handlePreScanFixNow,
    handleMarkExternal,
    handleStub,
    handleAllStubs,
    resetStates:     resetPreScanStates,
  } = usePreScanActions({
    analysisContext,
    apiBase,
    currentJobId,
    onFixNow:        (finding) => preScanFixNowRef.current?.(finding),
    onRerunComplete: (newJobId) => setCurrentJobId(newJobId),
  })

  // Internal back from remediation workspace
  const handleBackFromRemediation = useCallback(() => {
    setActiveFinding(null)
  }, [])

  // If a finding is selected, show the remediation workspace instead
  if (activeFinding) {
    return (
      <RemediationWorkspaceView
        analysisContext={{ ...analysisContext, gateType, jobId: currentJobId }}
        finding={activeFinding}
        onBack={handleBackFromRemediation}
        onFindingResolved={(findingId, resolution) => {
          setFindings(prev => prev.map(f => f.id === findingId ? { ...f, state: resolution } : f))
          setActiveFinding(null)
        }}
      />
    )
  }

  // Filtered findings
  const visibleFindings = findings.filter(f => {
    if (filterMode === FILTER_BLOCKING && f.severity !== 'BLOCK') return false
    if (filterMode === FILTER_WARNINGS && f.severity !== 'WARN')  return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        f.summary?.toLowerCase().includes(q) ||
        f.category?.toLowerCase().includes(q) ||
        f.cobolLocation?.file?.toLowerCase().includes(q) ||
        f.javaLocation?.file?.toLowerCase().includes(q)
      )
    }
    return true
  })

  const counts = status?.counts || { blocking: 0, warnings: 0, autoFixable: 0, needsDecision: 0 }

  // Blocking OPEN issues available for batch AI Resolve
  const batchCandidates = findings.filter(f => f.severity === 'BLOCK' && f.state === 'OPEN')

  // Group visible findings by file for the collapsible file-group UI
  const fileGroupMap = new Map()
  for (const finding of visibleFindings) {
    const file = finding.javaLocation?.file || '(unknown)'
    if (!fileGroupMap.has(file)) fileGroupMap.set(file, [])
    fileGroupMap.get(file).push(finding)
  }
  const fileGroups = [...fileGroupMap.entries()] // [[filename, findings[]], ...]

  return (
    <div className="gate-report-view">
      {/* Header */}
      <div className="gate-report-header">
        <button className="gate-report-back-btn" onClick={onBack}>
          <ArrowLeft size={14} />
          Back
        </button>
        <div className="gate-report-title-group">
          <h2 className="gate-report-title">
            <GateIcon size={18} />
            {gateLabel} Report
          </h2>
          <div className="gate-report-subtitle">Job: {currentJobId}</div>
        </div>
        {status && <StatusBadge status={status.status} />}
      </div>

      {/* Stats */}
      {status && (
        <div className="gate-report-stats">
          <div className="gate-stat-card blocking">
            <span className="gate-stat-value">{counts.blocking}</span>
            <span className="gate-stat-label">Blockers</span>
          </div>
          <div className="gate-stat-card warnings">
            <span className="gate-stat-value">{counts.warnings}</span>
            <span className="gate-stat-label">Warnings</span>
          </div>
          <div className="gate-stat-card fixable">
            <span className="gate-stat-value">{counts.autoFixable}</span>
            <span className="gate-stat-label">Auto-Fixable</span>
          </div>
          <div className="gate-stat-card decision">
            <span className="gate-stat-value">{counts.needsDecision}</span>
            <span className="gate-stat-label">Needs Decision</span>
          </div>
        </div>
      )}

      {/* Missing symbols pre-scan banner (compile gate, not dismissed, symbols exist) */}
      {gateType === 'compile' && !symbolBannerDismissed && missingSymbols.length > 0 && (
        <div className="gate-prescan-banner">
          <div className="gate-prescan-banner-header">
            <AlertTriangle size={14} className="gate-prescan-icon" />
            <strong>Missing symbols detected before compile</strong>
            <span className="gate-prescan-count">{missingSymbols.length} unresolved import{missingSymbols.length !== 1 ? 's' : ''}</span>
            {/* Generate All Stubs — upserts decisions for all unsettled symbols then one rerun */}
            {missingSymbols.some(s => actionStates[s.symbol] === null || actionStates[s.symbol] === 'error') && (
              <button
                className="gate-btn gate-btn--sm gate-btn--stub gate-prescan-generate-all-btn"
                onClick={() => handleAllStubs(missingSymbols)}
                disabled={preScanRunning}
                title="Generate compile-only stubs for all unresolved symbols and rerun compile check"
              >
                {preScanRunning
                  ? <><Loader2 size={10} className="spin"/> Generating…</>
                  : <><Wand2 size={10}/> Generate All Stubs (compile-only)</>
                }
              </button>
            )}
            <button
              className="gate-prescan-dismiss"
              onClick={() => setSymbolBannerDismissed(true)}
              title="Dismiss"
            >×</button>
          </div>
          <div className="gate-prescan-body">
            <table className="gate-prescan-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>File : Line</th>
                  <th>Category</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {missingSymbols.map(sym => {
                  const st = actionStates[sym.symbol]  // null|'saving_ext'|'external'|'stubbing'|'stubbed'|'error'
                  return (
                    <tr key={sym.symbol} className={`prescan-row prescan-${sym.guessedCategory}`}>
                      <td className="prescan-symbol" title={sym.symbol}>
                        {sym.symbol.split('.').pop()}
                        <span className="prescan-fqn">{sym.symbol}</span>
                      </td>
                      <td className="prescan-location">{sym.file}:{sym.line}</td>
                      <td className="prescan-category">
                        <span className={`prescan-cat-badge ${sym.guessedCategory}`}>
                          {sym.guessedCategory}
                        </span>
                      </td>
                      <td className="prescan-action-cell">
                        {/* Settled state — show tag instead of buttons */}
                        {st === 'external' && (
                          <span className="prescan-state-tag prescan-state-external">External dependency</span>
                        )}
                        {st === 'stubbed' && (() => {
                          const shortName  = sym.symbol.split('.').pop()
                          const pkgPath    = sym.package ? sym.package.replace(/\./g, '/') : null
                          const stubPath   = pkgPath
                            ? `src/main/java/${pkgPath}/${shortName}.java`
                            : `${shortName}.java`
                          return (
                            <span
                              className="prescan-state-tag prescan-state-stubbed"
                              title={`Stub file: ${stubPath}`}
                            >
                              <Check size={10}/> Stub applied
                              <span className="prescan-stub-path">{stubPath}</span>
                            </span>
                          )
                        })()}
                        {st === 'error' && (
                          <span className="prescan-state-tag prescan-state-error">Action failed — retry</span>
                        )}
                        {/* Active / unsettled — show 3 action buttons.
                            The button matching preScanDefaultAction (from DM node config)
                            receives the gate-btn--default-action class for visual emphasis. */}
                        {(st === null || st === 'saving_ext' || st === 'stubbing') && (
                          <div className="prescan-action-btns">
                            {/* 1. Fix now */}
                            <button
                              className={`gate-btn gate-btn--sm gate-btn--secondary${preScanDefaultAction === 'fix-now' ? ' gate-btn--default-action' : ''}`}
                              onClick={() => handlePreScanFixNow(sym)}
                              disabled={preScanRunning}
                              title={`Open Fix & Continue at ${sym.file}:${sym.line}${preScanDefaultAction === 'fix-now' ? ' (default)' : ''}`}
                            >
                              Fix now{preScanDefaultAction === 'fix-now' && <span className="prescan-default-badge">default</span>}
                            </button>
                            {/* 2. Generate compile-only stub */}
                            <button
                              className={`gate-btn gate-btn--sm gate-btn--stub${preScanDefaultAction === 'compile-only-stub' ? ' gate-btn--default-action' : ''}`}
                              onClick={() => handleStub(sym)}
                              disabled={preScanRunning || st === 'stubbing'}
                              title={`Create compile-only stub class and rerun compile check${preScanDefaultAction === 'compile-only-stub' ? ' (default)' : ''}`}
                            >
                              {st === 'stubbing'
                                ? <><Loader2 size={10} className="spin"/> Stubbing…</>
                                : <>Generate stub{preScanDefaultAction === 'compile-only-stub' && <span className="prescan-default-badge">default</span>}</>
                              }
                            </button>
                            {/* 3. Mark external */}
                            <button
                              className={`gate-btn gate-btn--sm${preScanDefaultAction === 'mark-external' ? ' gate-btn--default-action' : ''}`}
                              onClick={() => handleMarkExternal(sym)}
                              disabled={preScanRunning || st === 'saving_ext'}
                              title={`Record as external dependency — no stub generated${preScanDefaultAction === 'mark-external' ? ' (default)' : ''}`}
                            >
                              {st === 'saving_ext'
                                ? <><Loader2 size={10} className="spin"/> Saving…</>
                                : <>Mark external{preScanDefaultAction === 'mark-external' && <span className="prescan-default-badge">default</span>}</>
                              }
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {/* Footer — rerun status */}
            <div className="gate-prescan-footer">
              {preScanRerunStatus === 'rerunning' && (
                <span className="gate-prescan-rerun-status">
                  <Loader2 size={12} className="spin"/> Rerunning compile check…
                </span>
              )}
              {preScanRerunStatus === 'done' && (
                <span className="gate-prescan-rerun-status done">
                  <Check size={12}/> Compile rerun complete — report refreshed.
                </span>
              )}
              {preScanRerunStatus === 'error' && (
                <span className="gate-prescan-rerun-status error">
                  Rerun failed — check worker logs or try AI Resolve.
                </span>
              )}
              {!preScanRerunStatus && (
                <span className="gate-prescan-hint">
                  Generate stub reruns compile immediately. Mark external records a decision only.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No-improvement banner — shown after AI Resolve All when blockers didn't decrease */}
      {noImprovement && (
        <div className="gate-no-improvement-banner">
          <AlertTriangle size={14} />
          <span>
            {rolledBackCount > 0
              ? `Regression detected — AI patches increased errors. Rolled back ${rolledBackCount} file(s). Use "Open Fix & Continue" to review manually.`
              : `No improvement after AI Resolve${lastRerunJobId ? ` (job: ${lastRerunJobId.slice(-12)})` : ''}. Check worker logs for workspace write and Maven result.`
            }
          </span>
        </div>
      )}

      {/* Fixes saved banner + re-run button */}
      {(fixesSavedBanner || rerunning) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', margin: '0 0 8px',
          background: rerunning ? '#1a2a3a' : '#1a3a1f',
          border: `1px solid ${rerunning ? '#2a4a5a' : '#2a5a30'}`,
          borderRadius: 8, color: rerunning ? '#93c5fd' : '#4ade80', fontSize: 13
        }}>
          {rerunning
            ? <Loader2 size={15} className="spinning" />
            : <CheckCircle size={15} />
          }
          <span style={{ flex: 1 }}>
            {rerunning ? 'Re-running Compile Check…' : fixesSavedBanner}
          </span>
          {!rerunning && (
            <button
              onClick={handleRerunCompile}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', background: '#4f46e5', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 12,
                fontWeight: 600, cursor: 'pointer'
              }}
            >
              <Zap size={12} /> Re-run Compile Check
            </button>
          )}
          {!rerunning && (
            <button
              onClick={() => setFixesSavedBanner(null)}
              style={{
                background: 'none', border: 'none', color: '#6b7280',
                cursor: 'pointer', padding: 4, fontSize: 12
              }}
            >✕</button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="gate-report-filters">
        <span className="filter-label">
          <Filter size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Filter:
        </span>
        <div className="filter-toggle-group">
          <button
            className={`filter-toggle ${filterMode === FILTER_BLOCKING ? 'active' : ''}`}
            onClick={() => setFilterMode(FILTER_BLOCKING)}
          >
            Blocking only
          </button>
          <button
            className={`filter-toggle ${filterMode === FILTER_WARNINGS ? 'active' : ''}`}
            onClick={() => setFilterMode(FILTER_WARNINGS)}
          >
            Warnings
          </button>
          <button
            className={`filter-toggle ${filterMode === FILTER_ALL ? 'active' : ''}`}
            onClick={() => setFilterMode(FILTER_ALL)}
          >
            Show all
          </button>
        </div>
        <div className="filter-search">
          <Search size={13} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search file, symbol, category…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* AI Resolve All — only for compile/structural, when blocking issues exist */}
        {gateType !== 'package' && batchCandidates.length > 0 && (
          <button
            className="gate-resolve-all-btn"
            onClick={() => { if (isWebPortal()) return; resolveAll(batchCandidates); }}
            disabled={aiResolving || loading || isWebPortal()}
            title={isWebPortal() ? "Temporarily disabled in QA" : "Auto-run AI repair on all blocking issues, then re-run compile check"}
            style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
          >
            <Wand2 size={13} />
            AI Resolve All ({batchCandidates.length})
          </button>
        )}

        {/* Re-run Compile Check — always visible for compile gate */}
        {gateType === 'compile' && (
          <button
            onClick={handleRerunCompile}
            disabled={rerunning || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', background: rerunning ? '#1e2130' : '#4f46e5',
              color: '#fff', border: 'none', borderRadius: 6, fontSize: 12,
              fontWeight: 600, cursor: rerunning ? 'not-allowed' : 'pointer',
              opacity: rerunning ? 0.7 : 1, marginLeft: 4,
            }}
            title="Re-run compile check to verify fixes"
          >
            {rerunning
              ? <><Loader2 size={12} className="spinning" /> Compiling…</>
              : <><Zap size={12} /> Re-run Compile</>
            }
          </button>
        )}
      </div>

      {/* Rerunning status bar */}
      {rerunning && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', margin: '0 0 8px',
          background: '#1a2a3a', border: '1px solid #2a4a5a',
          borderRadius: 8, color: '#93c5fd', fontSize: 13
        }}>
          <Loader2 size={15} className="spinning" />
          <span>Re-running Compile Check with applied fixes…</span>
        </div>
      )}

      {/* Body */}
      <div className="gate-report-body">
        {loading && (
          <div className="gate-report-loading">
            <Loader2 size={24} className="spin" />
            Loading report…
          </div>
        )}
        {!loading && error && (
          <div className="gate-report-error">
            <XCircle size={20} />
            {error}
          </div>
        )}
        {!loading && !error && visibleFindings.length === 0 && (
          <div className="findings-empty">
            <CheckCircle size={32} style={{ color: 'var(--accent-green)' }} />
            <span>No findings match the current filter.</span>
          </div>
        )}
        {!loading && !error && visibleFindings.length > 0 && (
          <div className="findings-table" key={currentJobId}>
            {fileGroups.map(([filename, groupFindings]) => (
              <FileGroup
                key={filename}
                filename={filename}
                issues={groupFindings}
                onOpenEditor={handleOpenEditor}
                onAIResolveFile={
                  gateType === 'compile'
                    ? handleSmartAIResolve   // new Human-in-the-Loop flow
                    : handleAIResolveFile    // legacy flow for structural
                }
                aiResolving={aiResolving}
                gateType={gateType}
              />
            ))}
          </div>
        )}

        {/* Fix Card Panel — Smart AI Resolve (compile gate, human-in-the-loop) */}
        {fixPanelFile && analyzeState.phase !== STATE_IDLE && (
          <div className="fix-card-panel-overlay">
            <FixCardPanel
              analyzeState={analyzeState}
              onApplyFix={(card) => applyFix(card, currentJobId, handleFixMyself)}
              onFixMyself={handleFixMyself}
              onDismiss={() => {
                const appliedCount = (analyzeState.applied || []).length
                setFixPanelFile(null)
                dismissFixPanel()
                if (appliedCount > 0) {
                  setFixesSavedBanner(`${appliedCount} fix${appliedCount > 1 ? 'es' : ''} saved. Re-run Compile Check to verify.`)
                }
              }}
              STATE_LOADING={STATE_LOADING}
              STATE_READY={STATE_READY}
              STATE_APPLYING={STATE_APPLYING}
              STATE_APPLIED={STATE_APPLIED}
              STATE_ERROR={ANALYZE_ERROR}
            />
          </div>
        )}
      </div>

      {/* AI Resolve progress modal */}
      {resolveState.open && (
        <AIResolveModal
          resolveState={resolveState}
          onClose={closeModal}
          onCompleted={handleModalCompleted}
          onOpenFixAndContinue={(issue) => {
            closeModal()
            // When rollback occurred, the original file was restored — use currentJobId
            // (the pre-AI job) so Fix & Continue shows the original error count, not the
            // AI's broken rerun. Otherwise use newJobId (the successful post-rerun job).
            const wasRolledBack = (resolveState.rolledBack?.length ?? 0) > 0
            const jobIdForNav = wasRolledBack ? currentJobId : (resolveState.newJobId || currentJobId)
            if (import.meta.env.DEV)
              console.log(' [GateReportView] onOpenFixAndContinue', { jobIdForNav, newJobId: resolveState.newJobId, currentJobId })
            onResolveIssue?.(issue, jobIdForNav)
          }}
        />
      )}
    </div>
  )
}
